import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Translation, getI18n } from 'react-i18next';
import {
  Button,
  Modal,
  Row,
  Col,
  Steps,
  Typography,
  Empty,
  List,
  Spin,
  Input,
  Tag,
  Checkbox,
  Form,
} from 'antd';
import UserAvatar from '../../../elements/containers/UserAvatarContainer';
import useDebounce from '../../../elements/components/useDebounce';

import Logger from '../../../../../lib/Logger';
import './styles/TransferPopover.less';
import './styles/BulkSmsMessageModal.less';
import notification from '../../../elements/lib/NotificationWrapper';

const { Title, Text } = Typography;
const { TextArea } = Input;

const BulkSmsMessageModal = ({
  partnershipId,
  clearPages,
  load,
  limit,
  order,
  visible,
  setVisible,
  isLoading,
  list,
  total,
  createBulkSmsMessages,
  ...props
}) => {
  const [step, setStep] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [message, setMessage] = useState('');
  const debouncedSearchText = useDebounce(searchText, 1000);
  const scrollContainerRef = useRef(null);

  // Merge list with selected members to ensure they're always visible
  const mergedList = React.useMemo(() => {
    const listIds = new Set(list.map((m) => m.profile?.id));
    const missingSelected = selectedMembers.filter(
      (m) => !listIds.has(m.profile?.id)
    );
    return [...missingSelected, ...list];
  }, [list, selectedMembers]);

  const onClickCancel = () => {
    setVisible(false);
    setSelectedMembers([]);
    setSearchText('');
    setMessage('');
    setStep(0);
  };

  const handleNext = () => {
    if (selectedMembers.length > 0) {
      setStep(1);
    }
  };

  const handleBack = () => {
    setStep(0);
  };

  const handleSend = () => {
    if (!message.trim() || selectedMembers.length === 0) return;

    const userProfileIds = selectedMembers.map((member) => member.profile?.id);
    const data = {
      user_profile_ids: userProfileIds,
      body: message.trim(),
    };

    createBulkSmsMessages(partnershipId, data, (success) => {
      // Success callback
      if (success) {
        notification.success(
          getI18n().t('sms_messages_bulk_message_sent_success')
        );
        onClickCancel();
      } else {
        notification.error(
          getI18n().t('sms_messages_bulk_message_sent_failure')
        );
      }
    });
  };

  // Handle member selection toggle
  const handleMemberToggle = (member) => {
    const profileId = member.profile?.id;
    const isSelected = selectedMembers.some((m) => m.profile?.id === profileId);

    if (isSelected) {
      setSelectedMembers(
        selectedMembers.filter((m) => m.profile?.id !== profileId)
      );
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  // Handle tag removal
  const handleRemoveTag = (profileId) => {
    setSelectedMembers(
      selectedMembers.filter((m) => m.profile?.id !== profileId)
    );
  };

  // Check if member is selected
  const isMemberSelected = (profileId) => {
    return selectedMembers.some((m) => m.profile?.id === profileId);
  };

  // Load initial data when modal opens
  useEffect(() => {
    if (partnershipId && visible) {
      clearPages(partnershipId);
      setCurrentPage(1);

      const filterObject = {
        role: '3',
      };

      load(partnershipId, 1, limit, order, filterObject);
    }
  }, [partnershipId, visible, clearPages, load, limit, order]);

  // Handle search with debounce
  useEffect(() => {
    if (!partnershipId || !visible) return;

    if (debouncedSearchText.trim() !== '') {
      // When searching, clear and reload with search filter
      clearPages(partnershipId);
      setCurrentPage(1);

      const filterObject = {
        role: '3',
        search: debouncedSearchText,
      };

      load(partnershipId, 1, limit, order, filterObject);
    } else if (debouncedSearchText.trim() === '') {
      // When search is cleared, reload initial data
      clearPages(partnershipId);
      setCurrentPage(1);

      const filterObject = {
        role: '3',
      };

      load(partnershipId, 1, limit, order, filterObject);
    }
  }, [
    partnershipId,
    visible,
    debouncedSearchText,
    clearPages,
    load,
    limit,
    order,
  ]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loadingMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercentage > 0.8 && list.length < total) {
      setLoadingMore(true);
      const nextPage = currentPage + 1;

      const filterObject = {
        role: '3',
        ...(debouncedSearchText.trim() !== '' && {
          search: debouncedSearchText,
        }),
      };

      load(partnershipId, nextPage, limit, order, filterObject, () => {
        setLoadingMore(false);
        setCurrentPage(nextPage);
      });
    }
  }, [
    loadingMore,
    isLoading,
    list.length,
    total,
    currentPage,
    partnershipId,
    limit,
    order,
    debouncedSearchText,
    load,
  ]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <Translation>
      {(t) => (
        <>
          <Modal
            title={
              <Title level={3} className='add-resources-dashboard-title'>
                {t('sms_messages_bulk_message_title')}
              </Title>
            }
            centered
            visible={visible}
            onCancel={onClickCancel}
            footer={
              step === 0
                ? [
                    <Button key='cancel' onClick={onClickCancel}>
                      {t('action_close')}
                    </Button>,
                    <Button
                      key='next'
                      type='primary'
                      onClick={handleNext}
                      disabled={selectedMembers.length === 0}
                    >
                      {t('action_next')}
                    </Button>,
                  ]
                : [
                    <Button key='cancel' onClick={onClickCancel}>
                      {t('action_close')}
                    </Button>,
                    <Button key='back' onClick={handleBack}>
                      {t('action_back')}
                    </Button>,
                    <Button
                      key='send'
                      type='primary'
                      disabled={!message.trim()}
                      onClick={handleSend}
                    >
                      {t('action_send')}
                    </Button>,
                  ]
            }
            className='bulk-sms-message-modal'
            width={700}
            bodyStyle={{ paddingTop: 0 }}
            forceRender={true}
          >
            <div style={{ marginTop: '20px' }}>
              <Row
                justify='center'
                align='top'
                style={{ marginBottom: '30px' }}
              >
                <Col>
                  <Steps size='small' current={step}>
                    <Steps.Step
                      style={{ width: '500px' }}
                      title={t('Select members')}
                    />
                    <Steps.Step title={t('sms_messages_bulk_message_title')} />
                  </Steps>
                </Col>
              </Row>
            </div>

            {step === 0 ? (
              <>
                <div
                  className='search-input-container'
                  onClick={() =>
                    document.getElementById('member-search-input').focus()
                  }
                >
                  {selectedMembers.map((member) => (
                    <Tag
                      key={member.profile?.id}
                      closable
                      onClose={() => handleRemoveTag(member.profile?.id)}
                    >
                      {member?.profile?.first_name} {member?.profile?.last_name}
                    </Tag>
                  ))}
                  <Input
                    id='member-search-input'
                    placeholder={
                      selectedMembers.length === 0 ? 'Search member...' : ''
                    }
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    bordered={false}
                    suffix={
                      isLoading && searchText.trim() !== '' ? (
                        <Spin size='small' />
                      ) : null
                    }
                  />
                </div>

                <div
                  ref={scrollContainerRef}
                  className='members-list-container transfer-popover-scroll'
                >
                  {!isLoading && mergedList.length === 0 ? (
                    <div className='empty-state'>
                      <Empty
                        description={
                          <span>
                            {searchText.trim() !== ''
                              ? t('sms_messages_no_members_found')
                              : t('sms_messages_no_members')}
                          </span>
                        }
                      />
                    </div>
                  ) : (
                    <>
                      <div className='members-list-header'>
                        <Text>{t('sms_messages_all')}</Text>
                      </div>
                      <List
                        dataSource={mergedList}
                        renderItem={(member) => (
                          <List.Item
                            className='member-list-item'
                            onClick={() => handleMemberToggle(member)}
                          >
                            <List.Item.Meta
                              avatar={
                                <UserAvatar
                                  profile={member?.profile}
                                  size={22}
                                />
                              }
                              title={
                                <Text>
                                  {member?.profile?.first_name}{' '}
                                  {member?.profile?.last_name}
                                </Text>
                              }
                            />
                            <Checkbox
                              checked={isMemberSelected(member.profile?.id)}
                            />
                          </List.Item>
                        )}
                      />
                      {loadingMore && (
                        <div className='loading-more'>
                          <Spin size='small' />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className='message-step-container'>
                <Form layout='vertical'>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: 600 }}>
                        {t('sms_messages_bulk_message_title')}*
                      </span>
                    }
                    required={false}
                  >
                    <TextArea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('sms_messages_placeholder')}
                      autoSize={{ minRows: 6, maxRows: 10 }}
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        padding: '8px 12px',
                      }}
                    />
                  </Form.Item>
                </Form>
              </div>
            )}
          </Modal>
        </>
      )}
    </Translation>
  );
};

export default BulkSmsMessageModal;

Logger.log('silly', `BulkSmsMessageModal loaded.`);
